<template>
  <div class="variable-element">
    <div class="variable-header">
      <span class="variable-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="16 18 22 12 16 6"></polyline>
          <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
        <span>Variable</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="variable-content">
      <div class="variable-form">
        <div class="form-row">
          <div class="variable-select-container">
            <VariableSelector 
              v-model="selectedVariable"
              :variables="filteredVariables"
              placeholder="Sélectionnez une variable"
              @change="handleVariableChange"
            />
          </div>
          
          <div class="variable-input-container">
            <div class="input-wrapper">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tag-icon">
                <line x1="5" y1="9" x2="19" y2="9"></line>
                <line x1="5" y1="15" x2="19" y2="15"></line>
              </svg>
              <input 
                type="text" 
                v-model="localDefaultValue"
                class="variable-input"
                placeholder="Valeur par défaut"
                @blur="handleValueChange"
              >
            </div>
          </div>
        </div>
        <div class="variable-description">
          Définit la valeur de la variable
        </div>
      </div> 
    </div>
  </div>
</template>

<script>
import VariableSelector from '@/components/common/VariableSelector.vue';

export default {
  name: 'VariableInsertElement',
  components: {
    VariableSelector
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedVariable: this.getInitialVariable(),
      localDefaultValue: this.getInitialValue()
    }
  },
  computed: {
    filteredVariables() {
      return this.variables.filter(variable => variable.type !== 'global')
    }
  },
  watch: {
    // Re-synchronise l'UI quand l'élément change (insertion, refreshChain, Moo…)
    element: {
      deep: true,
      handler() {
        const v = this.getInitialVariable()
        const val = this.getInitialValue()
        if (v !== this.selectedVariable) this.selectedVariable = v
        if (val !== this.localDefaultValue) this.localDefaultValue = val
      }
    }
  },
  methods: {
    // Le slug de la variable. Forme canonique : element.variable = { id, value }.
    // Tolère l'ancienne forme string : element.variable = "slug".
    getInitialVariable() {
      const v = this.element.variable
      if (v && typeof v === 'object') return v.id || ''
      return v || ''
    },
    // La valeur. Forme objet : variable.value ; ancienne forme : element.defaultValue.
    getInitialValue() {
      const v = this.element.variable
      if (v && typeof v === 'object') return v.value || ''
      return this.element.defaultValue || ''
    },
    // Émet toujours la forme objet { id, value } (cohérente avec le template/Moo/runtime).
    emitUpdate() {
      this.$emit('update', {
        ...this.element,
        variable: {
          id: this.selectedVariable || '',
          value: this.localDefaultValue || ''
        }
      })
    },
    handleVariableChange() {
      this.emitUpdate()
    },
    handleValueChange() {
      this.emitUpdate()
    }
  }
}
</script>

<style scoped>
.variable-element {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.variable-element:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

.variable-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028; /* Peelo Dark Green */
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.variable-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #daffd1; /* Peelo Mint */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.variable-type-badge svg {
  color: #daffd1;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 255, 209, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #daffd1;
  border-color: #daffd1;
  transform: scale(1.05);
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

.variable-content {
  padding: 24px;
}

.variable-form {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.variable-select-container {
  flex: 2;
}

.variable-input-container {
  flex: 1;
}

.select-wrapper,
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 12px;
  padding: 0.25rem;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.select-wrapper:hover,
.input-wrapper:hover {
  border-color: rgba(20, 48, 40, 0.3);
  background: #ffffff;
}

.select-wrapper:focus-within,
.input-wrapper:focus-within {
  border-color: #143028;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(218, 255, 209, 0.4);
}

.tag-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #daffd1; /* Peelo Mint */
  color: #143028;      /* Peelo Dark Green */
  border-radius: 8px;
  font-size: 0.75rem;
  margin: 0 0.25rem;
}

.variable-select,
.variable-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #1e293b;
  outline: none;
}

.variable-select {
  appearance: none;
  padding-right: 2rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
}

.variable-description {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.75rem;
  padding-left: 0.5rem;
}

.edit-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid rgba(20, 48, 40, 0.2);
  border-radius: 6px;
  color: #143028;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.edit-button:hover {
  background: #daffd1;
  color: #143028;
  border-color: #143028;
}
</style>
